import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { getName, renderOrgOrUser } from "components/navigation/menu-bar";
import { BackgroundLetterAvatarsSquare } from "components/shared/shared";
import {
  GetUserIfLoggedIn,
  User,
  getActiveOrganisation,
  switchOrganisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchUserMemberships } from "data/queries";
import { ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router";
import { useIntercom } from "react-use-intercom";
import * as Routes from "routes";

interface OrgSwitcherProps {
  openMenu: boolean;
}

export const OrgSwitcher = (props: OrgSwitcherProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const [identityState, setIdentityState] = useIdentity();
  const activeOrg = getActiveOrganisation(identityState);
  const user: User | false = GetUserIfLoggedIn(identityState);
  const organisationList = user ? user.organisations : [];
  const history = useHistory();
  const { update, shutdown } = useIntercom();
  const identityName = getName(identityState);

  const membershipsQuery = useQuery({
    queryKey: [identityState, QueryKeys.userMembership],
    queryFn: () => fetchUserMemberships(true),
  });

  const getOrgSubscription = (orgId: string) => {
    // We do this awkward thing where we get it from the query cache because the subscription field on
    // the org object in identity may be stale as it can be fetched on login outside of react query
    // and may be stale
    return membershipsQuery.data?.find((o) => o.organisationId === orgId)
      ?.subscription;
  };

  return (
    <>
      {!props.openMenu && (
        <Box
          pl={2}
          pr={2}
          pt={1}
          pb={0}
          onClick={organisationList.length > 1 ? handleClick : () => {}}
        >
          <BackgroundLetterAvatarsSquare
            name={identityName}
            activeOrg={activeOrg}
            isLoading={false}
          />
        </Box>
      )}
      {props.openMenu && (
        <Box onClick={organisationList.length > 1 ? handleClick : () => {}}>
          <List
            sx={{
              display: "block",
              width: "226px",
              pl: 1,
              pr: 2,
              pt: 1,
              pb: 0,
            }}
          >
            <ListItemButton
              style={{
                justifyContent: "center",
                padding: "4px 8px 4px 8px", // The padding is related to the Avatar size
                height: "40px",
                width: props.openMenu ? "212px" : "56px",
                justifyItems: "center",
                gap: "8px",
              }}
            >
              <BackgroundLetterAvatarsSquare
                name={identityName}
                activeOrg={activeOrg}
                isLoading={false}
              />
              <Tooltip title={identityName} arrow placement="right">
                {identityName ? (
                  <>
                    <ListItemText
                      primaryTypographyProps={{
                        style: {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "120px",
                        },
                      }}
                      secondaryTypographyProps={{
                        style: {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "120px",
                        },
                      }}
                      primary={identityName}
                      secondary={
                        activeOrg
                          ? getOrgSubscription(activeOrg.organisationId) ??
                            activeOrg.subscription
                          : undefined
                      }
                      sx={{
                        display: props.openMenu ? "inline" : "none",
                      }}
                    />
                    {organisationList.length > 1 && <ChevronsUpDown />}
                  </>
                ) : (
                  <>
                    {props.openMenu && (
                      <Box
                        sx={{
                          width: "140px",
                        }}
                      >
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                      </Box>
                    )}
                  </>
                )}
              </Tooltip>
            </ListItemButton>
          </List>
        </Box>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        elevation={3}
        slotProps={{
          paper: {
            style: {
              backgroundImage: "none",
              width: "230px",
              maxHeight: "336px",
              overflowY: "auto",
              overflowX: "hidden",
              boxShadow:
                "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        {organisationList
          .filter((o) => o !== activeOrg)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((org, index) =>
            renderOrgOrUser(
              org.name,
              index,
              true,
              async () => {
                await switchOrganisation(
                  org,
                  identityState,
                  setIdentityState,
                  update
                ).finally(() => {
                  handleClose();
                  history.push(Routes.hub);
                });
              },
              getOrgSubscription(org.organisationId) ?? org.subscription,
              org.hasAccess !== false // Pass hasAccess flag
            )
          )}
      </Popover>
    </>
  );
};
